/**
 * <prescription-upload>
 *
 * 眼鏡処方箋アップロード欄の制御。
 * - 「度数あり」バリエーション選択時のみ表示・必須化し、それ以外では input を無効化（送信されない）。
 * - 選択の変更は variant-selects の change を即時監視（＋ variantChange をバックアップ購読）。
 * - 未アップロードでのカート追加を、product-form の submit を capture フェーズで横取りしてブロック。
 *   送信時は「その場で選択中オプション値」を同期的に読み取って判定するため、選択直後の
 *   素早いクリックでも取りこぼさない。
 *
 * 依存: constants.js（PUB_SUB_EVENTS）, pubsub.js（subscribe）— theme.liquid で先に読み込まれる。
 */
if (!customElements.get('prescription-upload')) {
  customElements.define(
    'prescription-upload',
    class PrescriptionUpload extends HTMLElement {
      constructor() {
        super();
        this.optionName = (this.dataset.optionName || '').trim();
        this.triggerValue = (this.dataset.triggerValue || '').trim();
        this.sectionId = this.dataset.sectionId || '';
        this.maxSizeMb = parseFloat(this.dataset.maxSizeMb || '20') || 20;
        this.input = this.querySelector('[data-prescription-file]');
        this.filenameEl = this.querySelector('[data-prescription-filename]');
        this.errorEl = this.querySelector('[data-prescription-error]');
        this.productForm = this.closest('product-form') || this.closest('form');
        // 変更監視は差し替えられない安定した祖先に付ける（variant-selects は変更時に DOM ごと差し替わる）
        this.optionScope = this.closest('product-info') || this.closest('.product') || document;

        this.onFileChange = this.onFileChange.bind(this);
        this.onOptionChange = this.onOptionChange.bind(this);
        this.onSubmitCapture = this.onSubmitCapture.bind(this);
      }

      connectedCallback() {
        if (this.input) this.input.addEventListener('change', this.onFileChange);

        // 選択オプションの変更を即時監視（安定祖先で change を拾う。ネットワーク往復を待たない）
        this.optionScope.addEventListener('change', this.onOptionChange);

        // バックアップ: variantChange（?variant= 付きロードなど、change を挟まない更新に対応）。
        // 判定は常に DOM ベースの currentSelectionIsRx() に統一（オプション名で厳密に絞り込む）。
        if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS && PUB_SUB_EVENTS.variantChange) {
          this.variantUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, () => {
            this.applyState(this.currentSelectionIsRx());
          });
        }

        // 未アップロード時のカート追加をブロック（capture フェーズで product-form より先に処理）
        if (this.productForm) {
          this.productForm.addEventListener('submit', this.onSubmitCapture, true);
        }

        // 初期状態を実際の選択値から同期
        this.applyState(this.currentSelectionIsRx());
      }

      disconnectedCallback() {
        if (this.variantUnsubscriber) this.variantUnsubscriber();
        if (this.optionScope) this.optionScope.removeEventListener('change', this.onOptionChange);
        if (this.productForm) this.productForm.removeEventListener('submit', this.onSubmitCapture, true);
      }

      /**
       * オプション値が「度数あり」を意味するかを判定する。
       * 商品ごとに「あり」「度数あり」と表記が揺れているため部分一致で見る
       * （「なし」「度数なし」は "あり" を含まないため誤判定しない）。
       * Liquid 側 (prescription-upload.liquid) の contains 判定と基準を揃えること。
       */
      matchesTrigger(value) {
        if (!this.triggerValue) return false;
        return (value || '').trim().indexOf(this.triggerValue) !== -1;
      }

      /**
       * variant-selects 内で「度数」オプションの選択値が「あり」かを同期的に読む。
       * オプション名でも絞り込むため、値「あり」を持つ他オプションと混同しない。
       * - ドロップダウン: <select name="options[度数]">
       * - ボタン/スウォッチ: <input type="radio" name="度数-{position}">（form 属性で紐付け）
       */
      currentSelectionIsRx() {
        // variant-selects は変更のたびに差し替わるため、毎回 DOM から取り直す
        const root = this.sectionId ? document.getElementById(`variant-selects-${this.sectionId}`) : null;
        if (!root) return !this.classList.contains('prescription-upload--hidden');

        // ドロップダウン
        const select = root.querySelector(`select[name="options[${this.optionName}]"]`);
        if (select) {
          return this.matchesTrigger(select.value);
        }

        // ラジオ（ボタン/スウォッチ）: name は "度数-1" のように {optionName}-{position}
        // Dawn のテンプレート整形により name 属性に改行が混じることがあるため trim する
        const namePrefix = `${this.optionName}-`;
        let isRx = false;
        root.querySelectorAll('input[type="radio"]:checked').forEach((el) => {
          const name = (el.getAttribute('name') || '').trim();
          if (name === this.optionName || name.indexOf(namePrefix) === 0) {
            if (this.matchesTrigger(el.value)) isRx = true;
          }
        });
        return isRx;
      }

      onOptionChange() {
        this.applyState(this.currentSelectionIsRx());
      }

      /** 度数あり(isRx=true)なら表示＋必須、そうでなければ非表示＋無効化（＝FormData から除外） */
      applyState(isRx) {
        this.classList.toggle('prescription-upload--hidden', !isRx);
        if (!this.input) return;
        this.input.disabled = !isRx;
        this.input.required = isRx;
        if (!isRx) this.clearError();
      }

      onFileChange() {
        this.clearError();
        const file = this.input && this.input.files ? this.input.files[0] : null;
        if (!file) {
          this.hideFilename();
          return;
        }
        if (file.size > this.maxSizeMb * 1024 * 1024) {
          this.input.value = '';
          this.hideFilename();
          this.showError(`ファイルサイズが大きすぎます。${this.maxSizeMb}MB 以下のファイルを選択してください。`);
          return;
        }
        if (this.filenameEl) {
          this.filenameEl.textContent = `選択中のファイル: ${file.name}`;
          this.filenameEl.hidden = false;
        }
      }

      onSubmitCapture(evt) {
        // 送信時にその場で選択状態を確定（レース回避）
        const isRx = this.currentSelectionIsRx();
        this.applyState(isRx);
        if (!isRx) return; // 度数なし・他商品はブロックしない

        const hasFile = this.input && this.input.files && this.input.files.length > 0;
        if (hasFile) return;

        evt.preventDefault();
        evt.stopPropagation();
        evt.stopImmediatePropagation();
        this.showError('眼鏡処方箋の画像または PDF をアップロードしてください。');
        this.scrollIntoView({ behavior: 'smooth', block: 'center' });
        if (this.input) this.input.focus();
      }

      hideFilename() {
        if (this.filenameEl) {
          this.filenameEl.hidden = true;
          this.filenameEl.textContent = '';
        }
      }

      showError(message) {
        if (!this.errorEl) return;
        this.errorEl.textContent = message;
        this.errorEl.hidden = false;
        this.classList.add('prescription-upload--error');
      }

      clearError() {
        if (!this.errorEl) return;
        this.errorEl.hidden = true;
        this.errorEl.textContent = '';
        this.classList.remove('prescription-upload--error');
      }
    }
  );
}
